import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileUploadOutput, PaymentDetailsMap } from '@zonofi/zono-money-store';
import { PaymentFieldKeys, PaymentModeType } from '../../../constants';
import { defaultPaymentDetails, paymentTypeFields } from '../../constant';
import { useParsePaymentImage } from './use-parse-payment-image';
import { useSavePayment } from './use-save-payment';
import { useParams } from 'react-router-dom';
import { PaymentInputField } from '../../payment-input';
import {
  MediaModalType,
  useMediaHandlers,
} from '../../../hooks/use-media-handlers';

export interface UsePaymentEditHandlersProps {
  paymentDetails: PaymentDetailsMap;
  setPaymentDetails: React.Dispatch<React.SetStateAction<PaymentDetailsMap>>;
  paymentState: {
    paymentMode: PaymentModeType | null;
    paymentType: string;
  };
  setPaymentState: React.Dispatch<
    React.SetStateAction<{
      paymentMode: PaymentModeType | null;
      paymentType: string;
    }>
  >;
  uploadedMedia: FileUploadOutput[];
  setUploadedMedia: React.Dispatch<React.SetStateAction<FileUploadOutput[]>>;
  isOnline?: boolean;
}

export const usePaymentEditHandlers = ({
  paymentDetails,
  setPaymentDetails,
  paymentState,
  setPaymentState,
  setUploadedMedia,
  uploadedMedia,
  isOnline,
}: UsePaymentEditHandlersProps) => {
  const { paymentId = '', customerId = '' } = useParams();
  const [saveLoader, setSaveLoader] = useState(false);

  const {
    selectedMedia,
    activeModal,
    imageParseState,
    onMediaPress,
    onThreeDotPress,
    mediaActions,
    uploadMediaActions,
    setActiveModal,
    setImageParseState,
    setSelectedMedia,
  } = useMediaHandlers({
    setUploadedMedia,
    paymentId,
    onAfterDelete: (lastModified, deletedParseId) => {
      if (lastModified === deletedParseId) {
        setPaymentState((prev) => ({
          ...prev,
          paymentMode: null,
        }));

        setPaymentDetails(
          defaultPaymentDetails.reduce((acc, field) => {
            acc[field.key] = '';
            return acc;
          }, {} as PaymentDetailsMap)
        );
      }
    },
    type: MediaModalType.PAYMENT,
  });

  useEffect(() => {
    if (!paymentId && isOnline && uploadedMedia?.length > 0) {
      setSelectedMedia(uploadedMedia[uploadedMedia?.length - 1]);
      setImageParseState((p) => ({ ...p, shouldParseImage: true }));
    }
  }, [uploadedMedia, paymentId]);

  const { parseImageFile } = useParsePaymentImage({
    selectedMedia,
    setPaymentState,
    setImageParseState,
    uploadedMedia,
  });

  const { onSavePayment } = useSavePayment({
    paymentDetails,
    paymentId,
    isOfflineUpdate: Boolean(customerId),
    paymentType: paymentState?.paymentType,
    uploadedMedia,
    setSaveLoader,
  });

  const handleFieldChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, value: any) => {
      setPaymentDetails((prev) => {
        const updated = { ...prev, [key]: value };
        if (key === PaymentFieldKeys.PAYMENT_MODE) {
          const mode = value as PaymentModeType;
          const newModeFields = paymentTypeFields[mode] || [];
          const updatedDetails: PaymentDetailsMap = {};
          [...defaultPaymentDetails, ...newModeFields].forEach((field) => {
            updatedDetails[field.key] = updated[field.key] ?? '';
          });

          return {
            ...updatedDetails,
            tempId: prev.tempId,
          };
        }
        return updated;
      });

      if (key === PaymentFieldKeys.PAYMENT_MODE) {
        setPaymentState((prev) => ({
          ...prev,
          paymentMode: value as PaymentModeType,
        }));
        setPaymentDetails((prev) => ({
          ...prev,
          paymentRefCode: String(Date.now()),
        }));
      }
    },
    [paymentTypeFields]
  );

  const renderPaymentFields = useMemo(() => {
    const defaultFields = [...defaultPaymentDetails];
    const selectedMode = paymentState?.paymentMode ?? '';

    const selectedModeFields = selectedMode
      ? paymentTypeFields[selectedMode as PaymentModeType]
      : [];

    const paymentModeFields = Array.isArray(selectedModeFields)
      ? [...selectedModeFields]
      : [];

    const narrationIndex = paymentModeFields.findIndex(
      (field) => field?.key === PaymentFieldKeys.NARRATION
    );

    const narrationField =
      narrationIndex >= 0
        ? paymentModeFields.splice(narrationIndex, 1)[0]
        : null;

    const finalPaymentFields = [
      ...defaultFields,
      ...paymentModeFields,
      ...(narrationField ? [narrationField] : []),
    ];

    return finalPaymentFields.map((field, index) => (
      <div key={`${field.key}-${index}`}>
        <PaymentInputField
          field={field}
          value={paymentDetails[field.key]}
          onChange={handleFieldChange}
          index={index}
        />
      </div>
    ));
  }, [paymentState.paymentMode, paymentDetails, handleFieldChange]);

  useEffect(() => {
    if (imageParseState.shouldParseImage && selectedMedia) {
      parseImageFile(selectedMedia);
    }
  }, [imageParseState.shouldParseImage, selectedMedia]);

  return {
    selectedMedia,
    activeModal,
    setActiveModal,
    onMediaPress,
    onThreeDotPress,
    mediaActions,
    uploadMediaActions,
    imageParseState,
    renderPaymentFields,
    saveLoader,
    onSavePayment,
  };
};
